import React, { useEffect, useState } from "react";
import axios from "axios";
import { Box, Button } from "@mui/material";
import ThemedSelect from "../../utils/ThemedSelect";
import ErrorModal from "../../utils/ErrorModal";

/* ---------- Direction C tokens (styling only) ---------- */
const FONT = "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif";
const MONO = "'IBM Plex Mono', ui-monospace, monospace";
const INK = "#0F1B2A";
const INK_2 = "#3D4C60";
const MUTED = "#66748A";
const LINE = "#E4E9EF";
const FIELD_EDGE = "#D5DEE7";
const PRIMARY = "#0B5C8A";

// Plan colours — visual only.
const PLAN_TONE = {
  FREE: { bg: "#F1F4F8", edge: LINE, ink: INK_2, dot: "#8494A8" },
  BASIC: { bg: "#EAF2FB", edge: "#C9DEF3", ink: "#1B4F82", dot: "#1F6FEB" },
  PRO: { bg: "#EFEDFB", edge: "#D3D0F2", ink: "#4A3FBF", dot: "#6C5CE7" },
};
const tone = (t) => PLAN_TONE[t] || PLAN_TONE.FREE;

const cardStyle = {
  background: "#FFFFFF",
  border: `1px solid ${LINE}`,
  borderRadius: 14,
  overflow: "hidden",
  marginBottom: 16,
};
const cardHeadStyle = {
  display: "flex",
  alignItems: "center",
  gap: 9,
  padding: "12px 16px",
  borderBottom: "1px solid #EDF1F5",
};
const h3Style = {
  margin: 0,
  fontFamily: FONT,
  fontSize: 15,
  fontWeight: 700,
  letterSpacing: "-0.01em",
  color: INK,
};
const dot = (c) => ({ width: 7, height: 7, borderRadius: "50%", background: c, flexShrink: 0 });
const thStyle = {
  padding: "10px 16px",
  textAlign: "left",
  background: "#F7F9FC",
  borderBottom: `1px solid ${LINE}`,
  fontFamily: FONT,
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: "0.09em",
  textTransform: "uppercase",
  color: MUTED,
};
const tdStyle = {
  padding: "12px 16px",
  borderBottom: "1px solid #F1F4F8",
  fontFamily: FONT,
  fontSize: 13.5,
  color: INK,
  verticalAlign: "middle",
};
const planPill = (t) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "2px 10px",
  borderRadius: 999,
  background: tone(t).bg,
  border: `1px solid ${tone(t).edge}`,
  color: tone(t).ink,
  fontFamily: FONT,
  fontSize: 11.5,
  fontWeight: 700,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  whiteSpace: "nowrap",
});
const featurePill = {
  display: "inline-flex",
  alignItems: "center",
  padding: "3px 10px",
  borderRadius: 999,
  background: "#FFFFFF",
  border: `1px solid ${LINE}`,
  color: INK_2,
  fontFamily: FONT,
  fontSize: 12.5,
  fontWeight: 600,
  textTransform: "capitalize",
};
const numInputStyle = {
  width: "100%",
  maxWidth: 160,
  height: 38,
  boxSizing: "border-box",
  padding: "0 10px",
  margin: 0,
  background: "#FFFFFF",
  border: `1px solid ${FIELD_EDGE}`,
  borderRadius: 9,
  fontFamily: MONO,
  fontSize: 13,
  color: INK,
  outline: "none",
};
const primaryBtnSx = {
  height: 40,
  px: 2.25,
  borderRadius: "10px",
  background: PRIMARY,
  color: "#FFFFFF",
  fontFamily: FONT,
  fontSize: 14,
  fontWeight: 600,
  textTransform: "none",
  boxShadow: "none",
  whiteSpace: "nowrap",
  "&:hover": { background: "#084767", boxShadow: "none" },
  "&.Mui-disabled": { background: PRIMARY, color: "#FFFFFF", opacity: 0.6 },
};
const labelStyle = {
  display: "block",
  marginBottom: 6,
  fontFamily: FONT,
  fontSize: 12,
  fontWeight: 600,
  color: INK_2,
};

const SubscriptionModal = () => {
  const storedUser = JSON.parse(localStorage.getItem("user"));

  const [features, setFeatures] = useState([]);
  const [limits, setLimits] = useState([]);
  const [loadingFeatures, setLoadingFeatures] = useState(false);
  const [savingFeatures, setSavingFeatures] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [notice, setNotice] = useState({ open: false, msg: "", type: "success" });
  const showNotice = (msg, type = "success") => setNotice({ open: true, msg, type });

  /* ===================== FEATURES ===================== */

  const fetchAllFeatures = async () => {
    setLoadingFeatures(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/subscription-modal/all`,
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } }
      );
      setFeatures(res.data || []);
    } catch (err) {
      console.error("Failed to fetch features", err);
    } finally {
      setLoadingFeatures(false);
    }
  };

  const toggleFeature = (type, featureName) => {
    setFeatures((prev) =>
      prev.map((f) =>
        f.type === type && f.feature === featureName
          ? { ...f, enabled: !f.enabled }
          : f
      )
    );
  };

  const saveFeatures = async () => {
    setSavingFeatures(true);
    try {
      const existingFeature = features.find(
        (f) => f.feature === selectedFeature
      );

      const prevType = existingFeature ? existingFeature.type : null;

      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/subscription-modal`,
        {
          type: selectedType,
          feature: selectedFeature,
          prevType: prevType,
        },
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } }
      );
      showNotice("Features updated successfully");
      setSelectedFeature("");
      setSelectedType("");
      fetchAllFeatures();
    } catch (err) {
      console.error(err);
      showNotice(err?.response?.data?.message || "Failed to update features", "error");
    } finally {
      setSavingFeatures(false);
    }
  };

  /* ===================== LIMITS ===================== */

  const fetchLimits = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/subscription-limit`,
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } }
      );
      setLimits(res.data || []);
    } catch (err) {
      console.error("Failed to fetch limits", err);
    }
  };

  const updateLimitValue = (type, field, value) => {
    setLimits((prev) =>
      prev.map((l) => (l.type === type ? { ...l, [field]: Number(value) } : l))
    );
  };

  const saveLimit = async (limit) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/subscription-limit`,
        limit,
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } }
      );
      showNotice(`${limit.type} limits updated`);
    } catch (err) {
      console.error(err);
      showNotice(err?.response?.data?.message || "Failed to update limits", "error");
    }
  };

  /* ===================== LIFECYCLE ===================== */

  useEffect(() => {
    fetchAllFeatures();
    fetchLimits();
  }, []);

  const SUBSCRIPTION_TYPES = ["FREE", "BASIC", "PRO"];

  const FEATURE_OPTIONS = ["LEAD_STRUCTURE", "LEAD_CUSTOM_FIELD", "LEAD"];

  const groupedFeatures = features.reduce((acc, feature) => {
    if (!acc[feature.type]) {
      acc[feature.type] = [];
    }
    acc[feature.type].push(feature.feature);
    return acc;
  }, {});
  /* ===================== UI ===================== */

  return (
    <div style={{ padding: "4px 0 24px", width: "100%", maxWidth: 900, fontFamily: FONT, color: INK }}>
      <h2 style={{ margin: "8px 0 4px", fontFamily: FONT, fontSize: 27, fontWeight: 800, letterSpacing: "-0.028em", color: INK }}>
        Subscription Configuration
      </h2>
      <div style={{ height: 16 }} />

      {/* ===================== FEATURES TABLE ===================== */}
      {/* ===================== ADD FEATURE FORM ===================== */}
      <div style={cardStyle}>
        <div style={cardHeadStyle}>
          <span style={dot("#1F6FEB")} />
          <h3 style={h3Style}>Add Feature to Subscription</h3>
        </div>
        <div style={{ padding: "14px 16px 16px" }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr auto" },
              gap: 1.5,
              alignItems: "end",
            }}
          >
            <div>
              <label style={labelStyle}>Feature</label>
              <ThemedSelect
                value={selectedFeature}
                onChange={(e) => setSelectedFeature(e.target.value)}
                placeholder="Select Feature"
                options={FEATURE_OPTIONS.map((f) => ({ value: f, label: f.replaceAll("_", " ") }))}
                height={40}
              />
            </div>

            <div>
              <label style={labelStyle}>Subscription Type</label>
              <ThemedSelect
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                placeholder="Select Subscription Type"
                options={SUBSCRIPTION_TYPES.map((t) => ({ value: t, label: t }))}
                height={40}
              />
            </div>
            <Button onClick={saveFeatures} disabled={savingFeatures} sx={primaryBtnSx}>
              {savingFeatures ? "Saving..." : "Save Feature Changes"}
            </Button>
          </Box>
        </div>
      </div>

      <div style={cardStyle}>
        <div style={cardHeadStyle}>
          <span style={dot("#6C5CE7")} />
          <h3 style={h3Style}>Subscription Features</h3>
        </div>

        {loadingFeatures ? (
          <p style={{ margin: 0, padding: "18px 16px", fontSize: 13.5, color: MUTED }}>Loading features...</p>
        ) : Object.keys(groupedFeatures).length > 0 ? (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ ...thStyle, width: 180 }}>Subscription Type</th>
                <th style={thStyle}>Features</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(groupedFeatures).map(([type, featureList]) => (
                <tr key={type}>
                  <td style={{ ...tdStyle, fontWeight: "bold" }}>
                    <span style={planPill(type)}>
                      <span style={dot(tone(type).dot)} />
                      {type}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {featureList.map((f) => (
                        <span key={f} style={featurePill}>{f.replaceAll("_", " ").toLowerCase()}</span>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p style={{ margin: 0, padding: "18px 16px", fontSize: 13.5, color: MUTED }}>No features configured</p>
        )}
      </div>

      {/* ===================== LIMITS TABLE ===================== */}
      <div style={cardStyle}>
        <div style={cardHeadStyle}>
          <span style={dot("#17A398")} />
          <h3 style={h3Style}>Subscription Limits</h3>
        </div>

        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", minWidth: 760, borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ ...thStyle, width: 180 }}>Subscription Type</th>
                <th style={thStyle}>Lead Limit</th>
                <th style={thStyle}>Project Limit</th>
                <th style={thStyle}>Monthly ₹</th>
                <th style={thStyle}>Yearly ₹</th>
                <th style={{ ...thStyle, textAlign: "right" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {limits.length > 0 ? (
                limits.map((l) => (
                  <tr key={l.type}>
                    <td style={tdStyle}>
                      <span style={planPill(l.type)}>
                        <span style={dot(tone(l.type).dot)} />
                        {l.type}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <input
                        type="number"
                        value={l.leadLimit || ""}
                        onChange={(e) =>
                          updateLimitValue(l.type, "leadLimit", e.target.value)
                        }
                        style={numInputStyle}
                      />
                    </td>
                    <td style={tdStyle}>
                      <input
                        type="number"
                        value={l.projectLimit || ""}
                        onChange={(e) =>
                          updateLimitValue(l.type, "projectLimit", e.target.value)
                        }
                        style={numInputStyle}
                      />
                    </td>
                    <td style={tdStyle}>
                      <input
                        type="number"
                        value={l.monthlyPrice ?? ""}
                        placeholder="0"
                        onChange={(e) =>
                          updateLimitValue(l.type, "monthlyPrice", e.target.value)
                        }
                        style={numInputStyle}
                      />
                    </td>
                    <td style={tdStyle}>
                      <input
                        type="number"
                        value={l.yearlyPrice ?? ""}
                        placeholder="0"
                        onChange={(e) =>
                          updateLimitValue(l.type, "yearlyPrice", e.target.value)
                        }
                        style={numInputStyle}
                      />
                    </td>
                    <td style={{ ...tdStyle, textAlign: "right" }}>
                      <Button
                        onClick={() => saveLimit(l)}
                        sx={{
                          height: 32,
                          px: 1.75,
                          borderRadius: "8px",
                          border: `1px solid ${FIELD_EDGE}`,
                          background: "#FFFFFF",
                          color: INK,
                          fontFamily: FONT,
                          fontSize: 13,
                          fontWeight: 600,
                          textTransform: "none",
                          "&:hover": { background: "#EAF2FB", borderColor: "#8FC3E6", color: "#1B4F82" },
                        }}
                      >
                        Save
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} style={{ ...tdStyle, color: MUTED, borderBottom: 0 }}>No limits configured</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ErrorModal
        open={notice.open}
        handleClose={() => setNotice((n) => ({ ...n, open: false }))}
        msg={notice.msg}
        type={notice.type}
      />
    </div>
  );
};

export default SubscriptionModal;